<template>
    <div class="explore-grid">
        <div class="explore-grid__header">
            <div class="explore-grid__count">{{ filteredCityCount }} Cities</div>
            <button class="explore-grid__sort" @click="toggleSort">
                <span>Score: High to Low</span>
                <span class="explore-grid__toggle" :class="{ 'explore-grid__toggle--active': livableCitySortHighToLow }">
                    <span class="explore-grid__toggle-thumb"></span>
                </span>
            </button>
        </div>

        <div class="explore-grid__items">
            <template v-for="item in displayItems">
                <a v-if="item.type === 'city'" :key="item.key" class="explore-grid__card" :href="getCityUrl(item.city.slug)">
                    <div class="explore-grid__card-img" :style="{ backgroundImage: `url(${getCityImage(item.city.slug)})` }">
                    </div>
                    <div class="explore-grid__card-info">
                        <div class="explore-grid__city-name">{{ item.city.name }}</div>
                    </div>
                    <div class="explore-grid__card-badge">
                        <span class="explore-grid__score">{{ item.city.score }}</span>
                        <span class="explore-grid__label">Overall</span>
                    </div>
                </a>
                <div v-else :key="item.key" class="explore-grid__inline-ad">
                    <AdComponent class="ad-width-100" :ads="item.ad" :showDebug="showAdDebug" :forceDebug="true"
                        :debugLabel="item.label" :debugPosition="item.position" />
                </div>
            </template>
        </div>

        <div v-if="showPagination" class="explore-grid__pagination">
            <button class="explore-grid__arrow" type="button" aria-label="Previous page" @click="changePage(-1)">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                        d="M0.258183 8.65754L7.13065 15.7438C7.48236 16.094 8.04284 16.0839 8.38252 15.7213C8.71387 15.3675 8.71387 14.8067 8.38252 14.4529L3.02089 8.9241H15.1147C15.6036 8.9241 16 8.51539 16 8.01122C16 7.50705 15.6036 7.09834 15.1147 7.09834H3.02089L8.38252 1.56949C8.73422 1.21924 8.74396 0.641323 8.40426 0.278684C8.06457 -0.0839541 7.50409 -0.0939924 7.15239 0.256264C7.14502 0.263607 7.13777 0.271082 7.13065 0.278685L0.258183 7.36536C0.216636 7.40806 0.179407 7.45501 0.147074 7.50548C0.133351 7.52694 0.12361 7.55113 0.111659 7.57304C0.0951118 7.60251 0.0803289 7.63299 0.0673923 7.66433C0.0564947 7.69417 0.0473309 7.72465 0.0399456 7.75561C0.0324211 7.78209 0.0222397 7.80765 0.0169287 7.83503C-0.00564384 7.95303 -0.00564384 8.07443 0.0169287 8.19243C0.0222406 8.21981 0.0324211 8.24492 0.0399456 8.27139C0.0473461 8.30235 0.0565119 8.33283 0.0673923 8.36268C0.0803289 8.39401 0.0951118 8.4245 0.111659 8.45397C0.12361 8.47633 0.133348 8.49961 0.147074 8.52152C0.17984 8.57039 0.217044 8.61594 0.258183 8.65754Z"
                        fill="black" />
                </svg>
            </button>
            <button class="explore-grid__arrow" type="button" aria-label="Next page" @click="changePage(1)">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                        d="M15.7418 8.65754L8.86935 15.7438C8.51764 16.094 7.95716 16.0839 7.61748 15.7213C7.28613 15.3675 7.28613 14.8067 7.61748 14.4529L12.9791 8.9241H0.885342C0.396381 8.9241 0 8.51539 0 8.01122C0 7.50705 0.396381 7.09834 0.885342 7.09834H12.9791L7.61748 1.56949C7.26578 1.21924 7.25604 0.641323 7.59574 0.278684C7.93543 -0.0839541 8.49591 -0.0939924 8.84761 0.256264C8.85498 0.263607 8.86223 0.271082 8.86935 0.278685L15.7418 7.36536C15.7834 7.40806 15.8206 7.45501 15.8529 7.50548C15.8666 7.52694 15.8764 7.55113 15.8883 7.57304C15.9049 7.60251 15.9197 7.63299 15.9326 7.66433C15.9435 7.69417 15.9527 7.72465 15.9601 7.75561C15.9676 7.78209 15.9778 7.80765 15.9831 7.83503C16.0056 7.95303 16.0056 8.07443 15.9831 8.19243C15.9778 8.21981 15.9676 8.24492 15.9601 8.27139C15.9527 8.30235 15.9435 8.33283 15.9326 8.36268C15.9197 8.39401 15.9049 8.4245 15.8883 8.45397C15.8764 8.47633 15.8667 8.49961 15.8529 8.52152C15.8202 8.57039 15.783 8.61594 15.7418 8.65754Z"
                        fill="black" />
                </svg>
            </button>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import AdComponent from '@/pages/components/AdComponent.vue'
import adMixin from '@/mixin/adMixin.js'

const CITY_IMAGE_MAP = {
    tokyo: require('@/assets/default/city-tokyo.jpg'),
    'new-york': require('@/assets/default/city-ny.jpg'),
    london: require('@/assets/default/city-london.jpg'),
    auckland: require('@/assets/default/city-auckland.jpg'),
    copenhagen: require('@/assets/default/city-copenhagen.jpg'),
    kuching: require('@/assets/default/city-kuching.jpg'),
    zurich: require('@/assets/default/city-zurich.jpg'),
    florence: require('@/assets/default/city-florence.jpg')
};
const PLACEHOLDER_IMAGE = require('@/assets/default/placeholder.png');

export default {
    name: 'ExploreGrid',
    components: {
        AdComponent
    },
    mixins: [adMixin],
    data() {
        return {
            currentPage: 0
        }
    },
    props: {
        inlineAds: {
            type: Object,
            default: () => ({})
        },
        showAdDebug: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        ...mapGetters(['filteredCities', 'filteredCityCount', 'livableCitySortHighToLow', 'livableCityData', 'deviceType']),
        isMobile() {
            return this.deviceType === 'mobile'
        },
        rowSize() {
            return this.isMobile ? 2 : 4
        },
        pageSize() {
            return 20
        },
        totalPages() {
            return Math.max(Math.ceil(this.filteredCities.length / this.pageSize), 1)
        },
        pageCities() {
            const start = this.currentPage * this.pageSize
            return this.filteredCities.slice(start, start + this.pageSize)
        },
        showPagination() {
            return this.filteredCities.length > this.pageSize
        },
        displayItems() {
            const items = []
            const adRows = this.isMobile ? [1, 4] : [2]
            this.pageCities.forEach((city, index) => {
                items.push({
                    type: 'city',
                    key: `city-${city.slug}`,
                    city
                })
                const rowIndex = Math.floor(index / this.rowSize) + 1
                if (index % this.rowSize === this.rowSize - 1 && adRows.includes(rowIndex)) {
                    const ad = this.inlineAds[`row${rowIndex}`]
                    if (!ad) {
                        return
                    }
                    const labelMap = this.isMobile
                        ? { 1: 'cities_ad_2', 4: 'cities_ad_3' }
                        : { 2: 'cities_ad_2' }
                    items.push({
                        type: 'ad',
                        key: `ad-row-${rowIndex}`,
                        ad,
                        label: labelMap[rowIndex] || `cities_ad_grid_row${rowIndex}`,
                        position: `Cities 卡片第${rowIndex}行后`
                    })
                }
            })
            return items
        },
        paginationKey() {
            return this.filteredCities.map((city) => city.slug).join('|')
        }
    },
    watch: {
        paginationKey() {
            this.currentPage = 0
        },
        totalPages(value) {
            if (this.currentPage < value) {
                return
            }
            this.currentPage = 0
        }
    },
    methods: {
        toggleSort() {
            this.$store.commit('setLivableCitySort', !this.livableCitySortHighToLow)
        },
        changePage(step) {
            if (!this.showPagination) {
                return
            }
            const next = this.currentPage + step
            if (next < 0) {
                this.currentPage = this.totalPages - 1
                return
            }
            this.currentPage = next % this.totalPages
        },
        getCityImage(slug) {
            const city = (this.livableCityData.cities || []).find((item) => item.slug === slug)
            return city?.image || CITY_IMAGE_MAP[slug] || PLACEHOLDER_IMAGE;
        },
        getCityUrl(slug) {
            const target = `/${slug}`;
            if (this.$route.params.channel) {
                return `/${this.$route.params.channel}${target}`;
            }
            return target;
        }
    }
};
</script>

<style lang="scss" scoped>
@import '~@/css/variables.scss';

.explore-grid {
    width: 100%;

    &__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: rem(24);
    }

    &__count {
        font-family: $font-family-serif;
        font-weight: 700;
        font-style: italic;
        font-size: rem(20);
        color: #000;
    }

    &__sort {
        background: none;
        border: none;
        display: flex;
        align-items: center;
        gap: rem(12);
        font-family: $font-family-serif;
        font-size: rem(16);
        color: #000;
        cursor: pointer;
        padding: 0;
    }

    &__toggle {
        width: rem(40);
        height: rem(24);
        border-radius: rem(12);
        background: #D1CDCD;
        position: relative;
        transition: background-color 0.3s;

        &--active {
            background: #fecd45;

            .explore-grid__toggle-thumb {
                transform: translateX(rem(16));
            }
        }
    }

    &__toggle-thumb {
        width: rem(16);
        height: rem(16);
        border-radius: 50%;
        background: #FFF7E1;
        position: absolute;
        top: rem(4);
        left: rem(4);
        transition: transform 0.3s;
        box-shadow: 0 rem(2) rem(4) rgba(0, 0, 0, 0.1);
    }

    &__items {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: rem(24);
    }

    &__inline-ad {
        grid-column: 1 / -1;
    }

    &__pagination {
        margin-top: rem(24);
        display: flex;
        justify-content: flex-end;
        gap: rem(24);
    }

    &__arrow {
        width: rem(40);
        height: rem(40);
        border: 0;
        border-radius: 50%;
        background: #fecd45;
        color: #000;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    &__card {
        position: relative;
        border-radius: rem(24);
        overflow: hidden;
        aspect-ratio: 1;
        box-shadow: 0 rem(2) rem(8) rgba(0, 0, 0, 0.1);
        display: block;
        text-decoration: none;

        &-img {
            width: 100%;
            height: 100%;
            background-size: cover;
            background-position: center;
        }

        &-info {
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            padding: rem(16);
            background: linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.8));
        }

        &-badge {
            position: absolute;
            top: rem(8);
            left: rem(8);
            background: #fecd45;
            border-radius: rem(12) rem(12) rem(12) rem(20);
            padding: rem(8);
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            width: rem(52);
        }
    }

    &__city-name {
        color: #fff;
        font-family: $font-family-serif;
        font-weight: 700;
        font-size: rem(20);
        text-shadow: 0 rem(2) rem(4) rgba(0, 0, 0, 0.2);
    }

    &__score {
        font-family: $font-family-serif;
        font-weight: 700;
        font-size: rem(16);
        line-height: rem(16);
        color: #000;
    }

    &__label {
        font-family: $font-family-serif;
        font-style: italic;
        font-size: rem(12);
        line-height: rem(12);
        color: #000;
    }
}

.detect_deviece_mobile {
    .explore-grid {
        &__items {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: rem(15);
        }

        &__card {
            border-radius: rem(16);
        }

        &__count {
            font-size: rem(16);
        }

        &__sort {
            font-size: rem(14);
        }

        &__pagination {
            justify-content: center;
            gap: rem(20);
        }
    }
}
</style>
